import React, { useContext } from 'react'
import flowRight from 'lodash/flowRight'
import upperFirst from 'lodash/upperFirst'
import pick from 'lodash/pick'
import withDialog from 'shared/hocs/withDialog'
import styled, { ThemeContext } from 'styled-components'
import Box from 'shared/components/Box'
import SvgCheckInIcon from 'assets/icons/CheckIn'
import SvgCheckOutIcon from 'assets/icons/Checkout'
import CheckInParticipantDialog, { CheckInForm } from './CheckInParticipant'
import { useDispatch } from 'react-redux'
import {
  hideAllDialogs,
  dialogProcessing,
  showDialog,
  hideDialog,
  setRefreshPage,
  showError,
} from 'shared/redux/app/reducer'
import useMutation, { useUpdateNode } from 'shared/hooks/useMutation'
import dayjs from 'dayjs'
import CheckInDetailsDialog from 'pages/CheckIn/components/Dialogs/CheckInDetails'
import CheckOutDetailsDialog from 'pages/CheckIn/components/Dialogs/CheckOutDetails'
import { ParticipantRowItem } from 'pages/Participants/model/participant'
import { PAGENAME } from 'pages/CheckIn/model/checkin'
import validator, { getEmptyCheckinFields } from 'pages/CheckIn/model/checkinField'
import checkOutValidator, { CheckOutFieldsType, getEmptyCheckoutFields } from 'pages/CheckIn/model/checkoutField'
import { organizationSettingSelector, roleSelector } from 'shared/hocs/withAuth'
import { useSelector } from 'react-redux'

export const participatFields = [
  'is_waiver_signed',
  'participant_id',
  'is_flagged',
  'hasfilled',
  'member_id',
  'name',
  'dob',
  'id',
  'gender',
  'school_not_applicable',
  'school_name',
  'contact_name',
  'phone_number',
  'email',
  'preferred_contact',
  'address',
  'first_name',
  'last_name',
  'photo_url',
]

function SelectCheckInOut() {
  const { isKiosk } = useSelector(roleSelector)
  const [, onCreate] = useMutation({ url: '/check-in/bulk', isBase: true })
  const [, onUpdate] = useUpdateNode({ node: 'check-in' })
  const [, onFetchScreeningusers] = useMutation({
    method: 'POST',
    url: `/note/user/daily/screening`,
    isBase: false,
  })
  const [, onCheckIn] = useMutation({
    method: 'POST',
    url: `/check-in`,
    isBase: false,
  })

  const { organization_information } = useSelector(organizationSettingSelector)
  const dispatch = useDispatch()
  const theme = useContext(ThemeContext)

  const handleCheckin = async (participant: ParticipantRowItem) => {
    await onCheckIn({
      data: {
        memberId: participant?.member_id,
        type: 'in',
        time: new Date().toISOString(),
        start: dayjs()
          .startOf('day')
          .toISOString(),
        end: dayjs()
          .endOf('day')
          .toISOString(),
      },
      onSuccess() {
        dispatch(hideDialog())
        dispatch(setRefreshPage(PAGENAME))
      },
      onError() {
        dispatch(dialogProcessing(false))
      },
    })
  }

  const handleCheckout = async (participant: ParticipantRowItem, data: CheckOutFieldsType) => {
    let payload = {
      data: {
        id: participant.id,
        check_out_time: new Date().toISOString(),
        pick_up_name: data.pick_up_name,
        pick_up_contact_number: data.pick_up_contact_number,
        pick_up_relationship: data.pick_up_relationship,
      },
      hideDialog: true,
      message: ``,
      onSuccess() {
        dispatch(hideDialog())
        dispatch(setRefreshPage(PAGENAME))
      },
    }

    onUpdate(payload)
  }

  const handleShowDialog = async (participant: ParticipantRowItem, pass: boolean = false) => {
    dispatch(
      showDialog({
        component: CheckInDetailsDialog,
        props: {
          validator,
          initialFields: {
            ...getEmptyCheckinFields(),
            isCheckedInAlready: pass,
          },
          hasCancel: true,
          continueLabel: pass ? '' : 'Check-in',
          cancelLabel: pass ? 'Done' : 'Cancel',
          participant: pick(participant, participatFields),
          onValid: async () => {
            await handleCheckin(participant)
          },
        },
      }),
    )
  }

  const handleShowCheckoutDialog = async (participant: ParticipantRowItem) => {
    dispatch(dialogProcessing(false))
    dispatch(
      showDialog({
        component: CheckOutDetailsDialog,
        props: {
          checkOutValidator,
          initialFields: {
            ...getEmptyCheckoutFields(),
          },
          isProcessing: false,
          hasCancel:
            participant.pick_up_permission != 'Child may travel to and from the facility independently' ? true : false,
          continueLabel:
            participant.pick_up_permission != 'Child may travel to and from the facility independently'
              ? 'Check-out'
              : 'Done',
          cancelLabel: 'Cancel',
          participant: pick(participant, participatFields),
          onValid: async (data: CheckOutFieldsType) => {
            if (participant.pick_up_permission != 'Child may travel to and from the facility independently') {
              await handleCheckout(participant, data)
            } else {
              dispatch(hideDialog())
            }
          },
        },
      }),
    )
  }

  const clickHandler = async (type: string) => {
    dispatch(hideAllDialogs())
    dispatch(
      showDialog({
        component: CheckInParticipantDialog,
        props: {
          continueLabel: `Check ${type === 'in' ? 'In' : 'Out'} Users`,
          title: `Check ${type === 'in' ? 'In' : 'Out'} Users to ${organization_information.name}`,
          checkType: type,
          initialFields: {
            selected: [],
          },
          onCancel: () => dispatch(hideAllDialogs()),
          onValid: async (data: CheckInForm) => {
            const pass = data.selected?.filter((each: ParticipantRowItem) => {
              if (type === 'out') {
                if (organization_information.is_daily_screening_form) {
                  return each.hasfilled && !each.is_flagged
                } else {
                  return !each.is_flagged
                }
              }

              if (organization_information.is_daily_screening_form) {
                return each.hasfilled && each.is_waiver_signed && !each.is_flagged
              } else {
                return each.is_waiver_signed && !each.is_flagged
              }
            })

            const fail = data.selected?.filter((each: ParticipantRowItem) => {
              if (type === 'out') {
                if (organization_information.is_daily_screening_form) {
                  return !each.hasfilled || each.is_flagged
                } else {
                  return each.is_flagged
                }
              }

              if (organization_information.is_daily_screening_form) {
                return !each.hasfilled || !each.is_waiver_signed || each.is_flagged
              } else {
                return !each.is_waiver_signed || each.is_flagged
              }
            })

            if (type === 'out') {
              if (!isKiosk) dispatch(hideDialog())

              if (
                data.selected.length == 1 &&
                data.selected[0].pick_up_permission == 'Child may travel to and from the facility independently'
              ) {
                const participant = data.selected[0]
                let payload = {
                  data: {
                    id: participant.id,
                    check_out_time: new Date().toISOString(),
                    pick_up_name: '',
                    pick_up_contact_number: '',
                    pick_up_relationship: '',
                  },
                  autohideTimeout: 2000,
                  message: `User Checked Out`,
                  subText: `${participant.full_name} \nsuccessfully checked out`,
                }

                onUpdate(payload)
              } else {
                data.selected.forEach(participant => {
                  if (participant.pick_up_permission == 'Child may travel to and from the facility independently') {
                    let payload = {
                      data: {
                        id: participant.id,
                        check_out_time: new Date().toISOString(),
                        pick_up_name: '',
                        pick_up_contact_number: '',
                        pick_up_relationship: '',
                      },
                      hideDialog: true,
                      message: ``,
                    }
                    onUpdate(payload)
                  }

                  handleShowCheckoutDialog(participant)
                })
              }

              return
            }

            if (pass.length < 1) {
              if (!isKiosk) dispatch(hideDialog())
              fail?.forEach(participant => {
                handleShowDialog(participant, false)
              })
            } else {
              await onCreate({
                data: {
                  type,
                  data: pass.map(e => ({
                    participant_id: e.id,
                    check_in_time: new Date().toISOString(),
                    start: dayjs()
                      .startOf('day')
                      .toISOString(),
                    end: dayjs()
                      .endOf('day')
                      .toISOString(),
                  })),
                },
                onSuccess() {
                  dispatch(setRefreshPage(PAGENAME))
                  dispatch(hideDialog())

                  pass?.forEach(participant => {
                    handleShowDialog(participant, true)
                  })

                  fail?.forEach(participant => {
                    handleShowDialog(participant, false)
                  })
                },
                onError(error: any) {
                  dispatch(hideDialog())
                  dispatch(
                    showError({
                      message: 'Check In Error',
                      subText: error?.message,
                      showSubText: true,
                    }),
                  )
                },
              })
            }
          },
        },
      }),
    )
  }

  return (
    <SelectCheckInOutContainer>
      <SelectOption
        onClick={() => {
          clickHandler('in')
        }}
      >
        <SvgCheckInIcon fill={theme.colors.main} />
        <Text>Check In Users</Text>
      </SelectOption>
      <SelectOption
        onClick={() => {
          clickHandler('out')
        }}
      >
        <SvgCheckOutIcon fill={theme.colors.main} />
        <Text>Check Out Users</Text>
      </SelectOption>
    </SelectCheckInOutContainer>
  )
}

const SelectCheckInOutDialog = flowRight(withDialog())(SelectCheckInOut)

SelectCheckInOutDialog.defaultProps = {
  hasContinue: false,
}

const SelectOption = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 120px;
  cursor: pointer;
  ${({ theme }) => `
    border: 2px solid ${theme.colors.main};
    border-radius: ${theme.borderRadius};
    background: ${theme.colors.tableAlternate};
  `}
`

const Text = styled(Box)`
  font-size: 16px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.main};
  line-height: 19px;
  margin-top: 25px;
`

const SelectCheckInOutContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding: 40px 40px 0;
`

export default SelectCheckInOutDialog
